import React, { useState } from 'react';
import { theme } from 'lib/style';
import {
  TableContextProvider,
  TableFooter,
  TablePaginationNew,
  TablePaginationSizeNew,
  Table,
} from 'lib/components';
import { Gap } from 'lib/components/styles/layout';
import { MdDeleteForever, MdEdit } from 'react-icons/md';
import { Button } from 'react-covideo-common';
import { PaginationConstants } from 'lib/const/PaginationConstants';
import { IHighlight } from 'lib/api/highlights/useQueryHighlights';
import { SortButtons } from 'app/pages/admin/components/SortButtons';
import {
  Layout,
  NormalText,
  PaginationWrapper,
  TableCell,
} from 'app/pages/admin/index.styled';
import {
  HIGHLIGHTS_SORT_KEYS,
  HighlightsTableColumns,
  getTableFields,
} from '../constants';
import { DeleteHighlightModal } from '../modal/DeleteHighlightModal';
import { useTranslation } from 'react-i18next';

type Props = {
  count: number;
  data: IHighlight[];
  page: number;
  size: number;
  onPaginationChange({ page, size }: { page: number; size: number }): void;
  onSortingUpdate(sortKey: string, order: PaginationConstants): void;
  sortKey: HIGHLIGHTS_SORT_KEYS | null;
  order?: PaginationConstants.ASCENDING | PaginationConstants.DESCENDING;
  handleEditClick: (highlight: IHighlight) => void;
};

export const HighlightTable = ({
  count,
  data,
  page,
  size,
  onPaginationChange,
  onSortingUpdate,
  order,
  sortKey,
  handleEditClick,
}: Props) => {
  const { t } = useTranslation();
  const [highlightToDelete, setHighlightToDelete] = useState<IHighlight | null>(
    null
  );

  const tableFields = getTableFields(t);
  return (
    <Layout>
      {highlightToDelete && (
        <DeleteHighlightModal
          highlight={highlightToDelete}
          onClose={() => setHighlightToDelete(null)}
        />
      )}
      <TableContextProvider
        total={count}
        onChange={onPaginationChange}
        initPage={page}
        initSize={size}
      >
        <div style={{ paddingBottom: '20px' }}>
          <Table
            compact={true}
            borderColor={theme.palette.neutral10}
            relative={true}
            headers={[
              ...tableFields.map((item, index) => {
                return (
                  <TableCell key={index} width={item.width} onClick={() => {}}>
                    {item.label}
                    {item.sortable && item.sortKey && (
                      <SortButtons
                        isSorted={sortKey === item.sortKey}
                        order={order}
                        onChange={value =>
                          onSortingUpdate(item.sortKey ?? '', value)
                        }
                      />
                    )}
                  </TableCell>
                );
              }),
              '',
            ]}
            hoverable={false}
            rows={data?.map((highlight: IHighlight, index: number) => ({
              key: index,
              columns: [
                ...tableFields.map((item, index) => {
                  let columnValue = <></>;
                  switch (item.value) {
                    case HighlightsTableColumns.TITLE:
                      columnValue = <NormalText>{highlight.title}</NormalText>;
                      break;
                    case HighlightsTableColumns.HIGHLIGHT:
                      columnValue = (
                        <NormalText>{highlight.description}</NormalText>
                      );
                      break;
                    default:
                      columnValue = <></>;
                  }
                  return (
                    <TableCell key={index} width={item.width}>
                      {columnValue}
                    </TableCell>
                  );
                }),
                <TableCell justifyContent={'flex-end'}>
                  <Gap
                    flexDirection='row'
                    gap='8px'
                    alignItems='start'
                    justifyContent='end'
                  >
                    <Button
                      onClick={() => handleEditClick(highlight)}
                      variant='primary'
                      icon={<MdEdit size={20} />}
                      size='small'
                    />
                    <Button
                      onClick={() => setHighlightToDelete(highlight)}
                      size='small'
                      variant='destructive'
                      icon={
                        <MdDeleteForever
                          color={theme.palette.buttonDelete}
                          size={20}
                        />
                      }
                    />
                  </Gap>
                </TableCell>,
              ],
              onClick: () => {},
            }))}
          />
        </div>
        <PaginationWrapper>
          <TableFooter>
            <TablePaginationNew />
            <TablePaginationSizeNew text={t('Show rows:')} />
          </TableFooter>
        </PaginationWrapper>
      </TableContextProvider>
    </Layout>
  );
};
